import React, { useState } from "react";
import {
  VStack,
  Heading,
  Button,
  Input,
  CardRoot,
  CardBody,
  Field,
  Box,
  Text,
  HStack,
  Link,
} from "@chakra-ui/react";
import { useColorModeValue } from "./ui/color-mode";
import navigationSound from "../assets/sounds/navigation.mp3";
import { useAccessibility } from "../context/AccessibilityContext";
import { FaEnvelope } from "react-icons/fa";

const ForgotPasswordComponent = ({ onBackToLogin }) => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const { soundEnabled } = useAccessibility();
  const cardBg = useColorModeValue("white", "gray.800");
  const headingColor = useColorModeValue("gray.700", "white");
  const errorColor = useColorModeValue("red.600", "red.400");
  const linkColor = useColorModeValue("blue.600", "blue.400");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const successBg = useColorModeValue("green.50", "green.900");
  const successColor = useColorModeValue("green.600", "green.400");
  const red50 = useColorModeValue("red.50", "red.900");

  const playSound = (sound) => {
    if (soundEnabled) {
      new Audio(sound).play();
    }
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = () => {
    setError("");

    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    playSound(navigationSound);
    console.log("Password reset requested for:", email);
    setSubmitted(true);
  };

  const handleBackToLogin = () => {
    playSound(navigationSound);
    if (onBackToLogin) {
      onBackToLogin();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <Box width="100%" maxW="md" mx="auto" mt={10} mb={10}>
      <CardRoot boxShadow="xl" borderRadius="xl" bg={cardBg}>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <Box textAlign="center" mb={2}>
              <Box
                as={FaEnvelope}
                fontSize="4xl"
                color={linkColor}
                mx="auto"
                mb={4}
              />
              <Heading
                as="h2"
                size="xl"
                textAlign="center"
                color={headingColor}
                mb={2}
              >
                Forgot Password?
              </Heading>
              <Text fontSize="sm" color={textColor}>
                No worries! Enter your email and we'll send you reset
                instructions.
              </Text>
            </Box>

            {submitted ? (
              <Box
                bg={successBg}
                color={successColor}
                p={4}
                borderRadius="md"
                textAlign="center"
              >
                <Text fontWeight="bold" mb={2}>
                  Reset Link Sent!
                </Text>
                <Text fontSize="sm">
                  Check your email for password reset instructions.
                </Text>
              </Box>
            ) : (
              <>
                {error && (
                  <Box
                    bg={red50}
                    color={errorColor}
                    p={3}
                    borderRadius="md"
                    fontSize="sm"
                  >
                    {error}
                  </Box>
                )}

                {/* Email Field */}
                <Field.Root>
                  <Field.Label fontSize="lg" fontWeight="bold" mb={2}>
                    Email Address
                  </Field.Label>
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    size="lg"
                    autoComplete="email"
                  />
                </Field.Root>

                {/* Submit Button */}
                <Button
                  colorScheme="blue"
                  size="lg"
                  width="full"
                  onClick={handleSubmit}
                  mt={2}
                >
                  Send Reset Link
                </Button>
              </>
            )}

            {/* Back to Login Link */}
            <Box textAlign="center" mt={4}>
              <HStack justifyContent="center" spacing={2}>
                <Text fontSize="sm" color={textColor}>
                  Remember your password?
                </Text>
                <Link
                  color={linkColor}
                  fontSize="sm"
                  fontWeight="bold"
                  onClick={handleBackToLogin}
                  cursor="pointer"
                  _hover={{ textDecoration: "underline" }}
                >
                  Back to Login
                </Link>
              </HStack>
            </Box>
          </VStack>
        </CardBody>
      </CardRoot>
    </Box>
  );
};

export default ForgotPasswordComponent;
